import type {
  BriefBuilderAnswers,
  TextStepDef,
  TerritoryId,
  TerritoryOption,
  TerritoryQuestions,
} from './types';

export const TERRITORIES: TerritoryOption[] = [
  { id: 'brand', title: 'Brand', description: 'What the brand means, and how it is performing' },
  { id: 'innovation', title: 'Innovation', description: 'A new offer, product, or way of working' },
  { id: 'packaging', title: 'Packaging', description: 'Designs, formats, and shelf presence' },
  { id: 'advertising', title: 'Advertising', description: 'Strategy, creative, or campaign results' },
  { id: 'positioning', title: 'Positioning', description: 'Where this sits against the alternatives' },
  { id: 'trend', title: 'Trend', description: 'A shift in the category or the culture' },
  { id: 'other', title: 'Other', description: 'None of those quite fits' },
];

const TERRITORY_QS: Record<TerritoryId, TerritoryQuestions> = {
  audience: {
    q1Label: 'Audience persona',
    q1Prompt: 'What behaviour or moment makes someone “your” customer — not a demographic, a situation?',
    q1Hint: 'The sharper the moment, the sharper the sample.',
    q2Label: 'Audience persona',
    q2Prompt: 'What do you already believe about them that you’re least sure of?',
    q2Hint: 'That uncertainty is often the real research question.',
  },
  brand: {
    q1Label: 'Brand',
    q1Prompt: 'What should people feel or say when they encounter your brand — and what do they say today?',
    q1Hint: 'Gap between intended and lived brand is usually the brief.',
    q2Label: 'Brand',
    q2Prompt: 'Which competitor or substitute do people reach for instead — and why, in their words?',
    q2Hint: 'Specific substitutes beat abstract category talk.',
  },
  pricing: {
    q1Label: 'Pricing',
    q1Prompt: 'What price (or range) are you considering — and what happens if you’re wrong by 20% either way?',
    q1Hint: 'Decision consequence makes the research useful.',
    q2Label: 'Pricing',
    q2Prompt: 'How do people justify paying (or not) today — value, status, habit, or something else?',
    q2Hint: 'The justification language often matters more than the number.',
  },
  innovation: {
    q1Label: 'Innovation & new ideas',
    q1Prompt:
      'What stage is the idea at — napkin sketch, written concept, working prototype, or already in someone’s hands?',
    q1Hint: 'The stage dictates the method. A napkin sketch needs exploration; a working prototype needs evaluation.',
    q2Label: 'Innovation & new ideas',
    q2Prompt:
      'What’s the one belief that has to be true for this to work — and how do people solve this problem today without you?',
    q2Hint: 'Walk through the last real example you know of. Specifics beat summaries.',
  },
  packaging: {
    q1Label: 'Packaging',
    q1Prompt: 'What must packaging achieve — stand out, explain, protect, signal quality, or something else?',
    q1Hint: 'Pick the primary job; secondary jobs are trade-offs.',
    q2Label: 'Packaging',
    q2Prompt: 'Where will someone first see or handle it — shelf, delivery unboxing, digital thumbnail?',
    q2Hint: 'Context changes what “good” looks like.',
  },
  trend: {
    q1Label: 'Trend',
    q1Prompt: 'What shift are you reacting to, and who is already moving?',
    q1Hint: '',
    q2Label: 'Trend',
    q2Prompt: 'What would tell you this is a fad rather than a change in behaviour?',
    q2Hint: '',
  },
  positioning: {
    q1Label: 'Positioning',
    q1Prompt: 'What is this meant to be, against the alternatives people already use?',
    q1Hint: '',
    q2Label: 'Positioning',
    q2Prompt: 'What would make someone switch, and what would make them stay?',
    q2Hint: '',
  },
  advertising: {
    q1Label: 'Advertising',
    q1Prompt: 'What is the ad or campaign supposed to change — awareness, consideration, belief, or behaviour?',
    q1Hint: 'One primary change keeps the brief honest.',
    q2Label: 'Advertising',
    q2Prompt: 'What have you already tried, and what did you learn (even if it failed)?',
    q2Hint: 'Past creative and results prevent repeating spend.',
  },
  other: {
    q1Label: 'Your project',
    q1Prompt: 'In one paragraph, what is the research really for — and what would “done well” look like?',
    q1Hint: 'We’ll shape method from this.',
    q2Label: 'Your project',
    q2Prompt: 'What must we not get wrong — politically, commercially, or ethically?',
    q2Hint: 'Constraints belong in the brief, not as surprises later.',
  },
};

export function territoryQuestions(id: TerritoryId | ''): TerritoryQuestions {
  if (!id) return TERRITORY_QS.other;
  return TERRITORY_QS[id] || TERRITORY_QS.other;
}

export function territoryTitle(id: TerritoryId | ''): string {
  return TERRITORIES.find((t) => t.id === id)?.title || 'Your project';
}

/** Mark's original 11 discovery questions. The wording and follow-ups stay as he approved them. */
export function buildSteps(_answers: BriefBuilderAnswers): TextStepDef[] {
  return [
    {
      id: 'decision',
      number: '01',
      label: 'The decision',
      kind: 'text',
      prompt: 'What decision are you trying to make — and what will you do differently once you have the answer?',
      hint: '',
      placeholder: 'Whether to…',
      answerKey: 'decision',
      allowUnknown: true,
    },
    {
      id: 'territory_q1',
      number: '02',
      label: 'The trigger',
      kind: 'text',
      prompt: "What's happened, or what's happening right now, that's brought this to your desk?",
      hint: '',
      example:
        "‘We keep getting complaints about the size of our chicken pieces.’ / ‘Our tracking shows a competitor’s share jumping.’ / ‘Our last promotion didn’t move sales.’",
      encouragement: "Good start. Let's get into what's actually going on.",
      placeholder: 'This came up because…',
      answerKey: 'territoryQ1',
      allowUnknown: false,
    },
    {
      id: 'brand_impact',
      number: '03',
      label: 'The brand',
      kind: 'text',
      prompt: 'How is this affecting your brand? Tell me more — sales, reputation, market share, whatever it is.',
      hint: '',
      encouragement: 'Makes sense. Now, how has this actually hit the brand?',
      placeholder: 'Sales, reputation, market share…',
      answerKey: 'brandImpact',
      allowUnknown: true,
    },
    {
      id: 'territory',
      number: '04',
      label: 'The territory',
      kind: 'territory',
      prompt: 'What best describes what this is about?',
      hint: '',
      encouragement: 'A quarter of the way there.',
      answerKey: 'territory',
      allowUnknown: false,
    },
    {
      id: 'insider_fact',
      number: '05',
      label: 'Competitors',
      kind: 'text',
      prompt:
        "Who do you see as your main competitors here — and is there anything they're doing that's caught your attention?",
      hint: "If nothing specific comes to mind, that's worth knowing too — just say so.",
      encouragement: 'Good — now, who else is in this picture?',
      placeholder: 'A brand, a habit, doing nothing…',
      answerKey: 'insiderFact',
      allowUnknown: true,
    },
    {
      id: 'honest_question',
      number: '06',
      label: 'Gut feeling',
      kind: 'text',
      prompt:
        "What's your gut feeling about why this is happening? Have you noticed anything, heard anything, or seen any past research that points to a reason?",
      hint: '',
      encouragement: 'Got it. Now your honest gut take — no need to be right, just honest.',
      placeholder: 'Even a hunch is useful here...',
      answerKey: 'honestQuestion',
      allowUnknown: true,
      addon: {
        key: 'leastSure',
        label: 'Which part of that are you least sure of? (optional)',
        placeholder: 'e.g. Whether loyalists will actually cut back, or just shift a few items elsewhere',
      },
    },
    {
      id: 'people',
      number: '07',
      label: 'Who to talk to',
      kind: 'text',
      prompt:
        "Who should this research be done with — who can actually give you the insight or understanding you're after?",
      hint: '',
      encouragement: 'Halfway there.',
      placeholder: 'e.g. Existing customers, lapsed users, category shoppers...',
      answerKey: 'people',
      allowUnknown: true,
    },
    {
      id: 'what_you_know',
      number: '08',
      label: 'Already looked at',
      kind: 'text',
      prompt:
        'What have you already looked into to answer this — a report, a past study, anything — and where did that leave you?',
      hint: '',
      encouragement: "Good. Let's check what you've already got.",
      placeholder: 'A report, a study, a round of demos…',
      answerKey: 'whatYouKnow',
      allowUnknown: true,
      addon: {
        key: 'dataHeld',
        label: "Any data you already hold that hasn't been looked at with this question in mind? (optional)",
        placeholder: 'e.g. Loyalty transactions, CRM, viewing or usage logs, past trackers...',
      },
    },
    {
      id: 'ideas',
      number: '09',
      label: 'Ideas',
      kind: 'choice',
      prompt:
        'Do you already have a few ideas of what you could do about this, or is figuring that out part of what you need the research for?',
      hint: '',
      encouragement: 'Almost done — just the practical bits left.',
      answerKey: 'ideas',
      choices: [
        { value: 'I have a few ideas', label: 'I have a few ideas', hint: "We'll ask what they are" },
        {
          value: "That's what I need the research to figure out",
          label: "That's what I need the research to figure out",
          hint: 'No follow-up needed',
        },
      ],
    },
    {
      id: 'practicalities',
      number: '10',
      label: 'When and who',
      kind: 'when',
      prompt: 'When do you need an answer — and who needs to see it?',
      hint: '',
      encouragement: 'Two to go.',
      placeholder: 'e.g. Within a few weeks, for the leadership team',
      answerKey: 'practicalities',
    },
    {
      id: 'optional',
      number: '11',
      label: 'Anything else',
      kind: 'text',
      prompt: "Is there anything else on your mind that I haven't asked about?",
      hint: '',
      encouragement: "Last one — and it's optional.",
      placeholder: 'Optional — anything at all...',
      answerKey: 'optional',
      optional: true,
    },
  ];
}

/** Local fallback when AI mid-check is unavailable (guest / offline). */
export function midCheckNarrative(answers: BriefBuilderAnswers): {
  text: string;
  highlights: string[];
  flags: string[];
} {
  const decision = tidyPhrase(answers.decision) || 'a decision you have not named yet';
  const territory = tidyPhrase(answers.territoryLabel) || 'a project';
  const thinking = tidyPhrase(answers.honestQuestion) || 'a hunch you have not named yet';
  const people = tidyPhrase(answers.people) || 'people you have not described yet';

  const highlights = [decision, territory, thinking, people].filter((h) => h.length >= 3);
  const text = `The research problem is ${decision}, framed as ${territory}. Your current thinking: ${thinking}. The person to talk to: ${people}.`;

  return { text, highlights, flags: [] };
}

function tidyPhrase(value: string): string {
  let s = (value || '').replace(/\s+/g, ' ').trim();
  s = s.replace(/\.{2,}/g, '.');
  s = s.replace(/,+/g, ',');
  return s;
}

export const BUDGET_OPTIONS: { id: BriefBuilderAnswers['practicalities']['budget']; label: string }[] = [
  { id: 'very_tight', label: 'Very tight' },
  { id: 'tight', label: 'Tight' },
  { id: 'comfortable', label: 'Comfortable' },
  { id: 'stretch', label: 'A stretch' },
  { id: 'significant', label: 'Significant investment' },
];

export const TARGET_SECONDS = 10 * 60;

export const GUEST_BUILDER_ANSWERS_KEY = 'guestBriefBuilderAnswers';
export const GUEST_BUILDER_STEP_KEY = 'guestBriefBuilderStep';
export const GUEST_BUILDER_NAME_KEY = 'guestBriefBuilderName';
